/**
 * Trading Settings Types
 * 交易设置类型定义
 */

/**
 * 交易设置配置
 */
export interface TradingSettings {
  // 基础信息
  brokerUid: string;             // 交易商UID（8位数字）
  brokerName: string;            // 交易商名称
  assignedDate: number;          // 分配日期（时间戳）
  
  // 费率设置
  fees: {
    maker: number;               // Maker费率 (0.0002 = 0.02%)
    taker: number;               // Taker费率 (0.0005 = 0.05%)
    deposit: number;             // 充值费率 (0.03 = 3%)
  };
  
  // 交易商回馈
  brokerRebate: {
    enabled: boolean;            // 启用交易商回馈
    rate: number;                // 回馈比例 (0.02 = 2%)
  };
  
  // 杠杆设置
  leverage: {
    max: number;                 // 最大杠杆 (1-125)
  };
  
  // 交易限制
  limits: {
    minOrderSize: number;        // 最小交易量 (BTC)
    maxOrderSize: number;        // 最大交易量 (BTC)
  };
  
  // 风控设置
  riskControl: {
    liquidationThreshold: number;    // 强平维持率阈值 (10-100)
  };
  
  // 滑点设置
  slippage: {
    enabled: boolean;            // 启用滑点
    maxSlippage: number;         // 最大滑点 (0-1, 如 0.001 = 0.1%)
    probability: number;         // 滑点概率 (0-100)
  };
  
  // 个别对冲配置（优先于全局配置）
  hedgeOverride?: {
    enabled: boolean;            // 是否启用个别配置
    ratio: number;               // 个别对冲比例 (0-100%)
  };
  
  // 元数据
  updatedAt: number;             // 最后更新时间
  updatedBy: string;             // 更新人（交易商UID）
}

/**
 * 默认交易设置
 * 注意：
 * - Maker/Taker 费率应该从交易商获取，无交易商时为 0
 * - 充值费率由系统管理员设定，无交易商时默认 3%
 * - 交易商回馈应该从交易商获取，无交易商时为 0
 */
export const DEFAULT_TRADING_SETTINGS: Omit<TradingSettings, 'brokerUid' | 'brokerName' | 'assignedDate' | 'updatedAt' | 'updatedBy'> = {
  fees: {
    maker: 0,       // 0% (无交易商时免费)
    taker: 0,       // 0% (无交易商时免费)
    deposit: 0.03,  // 3% (系统默认充值费率)
  },
  brokerRebate: {
    enabled: false,
    rate: 0,        // 0% (无交易商时无回馈)
  },
  leverage: {
    max: 100,       // 100倍
  },
  limits: {
    minOrderSize: 0.001,      // 0.001 BTC
    maxOrderSize: 10,         // 10 BTC
  },
  riskControl: {
    liquidationThreshold: 50,     // 50%
  },
  slippage: {
    enabled: true,
    maxSlippage: 0.001,       // 0.1%
    probability: 30,          // 30%
  },
};

/**
 * 交易设置辅助说明
 */
export const TRADING_SETTINGS_HELP = {
  brokerUid: '分配给该用户的交易商UID（8位数字），决定用户归属于哪个交易商',
  brokerName: '交易商的显示名称',
  
  makerFee: 'Maker手续费：挂限价单时收取的费率，为市场提供流动性，费率较低',
  takerFee: 'Taker手续费：市价单或立即成交的限价单收取的费率，消耗市场流动性，费率较高',
  depositFee: '充值费率：用户充值时扣除的手续费。例如3%表示充值100元，实际到账97元',
  
  brokerRebateEnabled: '是否启用交易商回馈：交易商可补贴部分充值费用给用户',
  brokerRebateRate: '交易商回馈比例：交易商补贴给用户的百分比。例如2%表示充值100元，交易商补贴2元给用户',
  
  maxLeverage: '用户可使用的最大杠杆倍数。杠杆越高，收益和风险都会放大。建议新手使用较低杠杆（如10-20倍）',
  
  minOrderSize: '单笔交易的最小数量限制，低于此值的订单将被拒绝',
  maxOrderSize: '单笔交易的最大数量限制，超过此值的订单将被拒绝',
  
  liquidationThreshold: '强制平仓维持率阈值：当维持率低于此值时，系统将自动平仓。维持率 = (净值 / 冻结保证金) × 100%',
  
  slippageEnabled: '是否启用滑点模拟。滑点是指实际成交价格与预期价格之间的差异，常见于市价单或大额交易',
  maxSlippage: '最大滑点百分比：实际成交价格最多可偏离预期价格的百分比',
  slippageProbability: '滑点发生概率：每笔交易发生滑点的可能性',
  
  depositExample: '充值示例：充值费率3%，交易商回馈2%\n- 用户充值：100元\n- 扣除费用：100 × 3% = 3元\n- 实际到账：100 - 3 = 97元\n- 交易商回馈：100 × 2% = 2元\n- 最终余额：97 + 2 = 99元',
};

